import {
  faDesktop,
  faFlask,
  faFlaskVial,
  faHouseMedical,
  faKitMedical,
  faSignalStream,
  faSyringe,
  faUserMd,
  faWavePulse,
} from '@fortawesome/pro-light-svg-icons';
import { useRouter } from 'next/navigation';
import { useStore } from 'zustand';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { SearchType } from '@/types/most-search-type';
import {
  RecentSearches,
  SearchResult,
} from '@/app/(main)/_components/search-modal/search-modal.types';
import { Icon } from '@/components/ui/icon.component';
import {
  appendRecentSearches,
  setSelectedSearch,
} from '@/store/search/search.store';
import { useRecentSearchesStorage } from '@/storage/recents-searches.storage';
import { Avatar } from '@/components/ui/avatar/avatar.component';
import {
  ProgressiveSearchModalKeys,
  SelectedProcedures,
} from '@/app/(main)/(pages)/_components/search/search.component';
import { useProgressiveModal } from '@/components/ui/progressive-modal/progressive-modal.hook';
import { useSearch } from '@/app/_hooks/search/use-search.hook';
import { OverridedUserLocation } from '@/utils/formatters/city/format-place-from-slug.const';
import { userLocationStore } from '@/store/user-location.store';
import { setOpenLocationSelectionModal } from '@/store/modal';
import { useSearchParams } from '@/hooks/use-search-params.hook';
import { PersistedUserNavigationSearchParams } from '@/types/enums/persisted-user-navigation-search-params.enum';
import { slugify } from '@/utils/formatters/slugfy';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { checkLaboratorialOrVaccineSearch } from '@/app/_utils/check-laboratorial-or-vaccine-search.util';

const titleMap = {
  PRESENTIAL_CONSULTATION: 'Atendimento presencial',
  ONLINE_CONSULTATION: 'Teleconsulta',
  IMAGE_EXAM: 'Exame de imagem',
  EMERGENCY: 'Pronto atendimento',
  SURGERY: 'Cirurgia',
};

export const resolveTitle = (
  searchResult: SearchResult,
  inRecentSearch: boolean = false,
): string => {
  if (
    searchResult.searchType
    && titleMap[searchResult.searchType as keyof typeof titleMap]
  ) {
    return titleMap[searchResult.searchType as keyof typeof titleMap];
  }

  if (searchResult.searchType === SearchType.LABORATORIAL_EXAM) {
    if (!inRecentSearch) {
      return 'Exame laboratorial';
    }
    return [
      'Exame laboratorial',
      `${searchResult.id?.split(',').length} exames`,
    ]
      .filter(Boolean)
      .join(' · ');
  }

  if (searchResult.searchType === SearchType.VACCINE) {
    if (!inRecentSearch) {
      return 'Vacina';
    }

    return ['Vacina', `${searchResult.id?.split(',').length} Vacinas`]
      .filter(Boolean)
      .join(' · ');
  }

  if (searchResult.searchType === SearchType.DOCTOR) {
    const specialtiesSeparatedByComma = searchResult?.specialties
      ?.slice(0, 2)
      .join(', ');
    return ['Profissional', specialtiesSeparatedByComma].join(' - ');
  }

  if (searchResult.searchType === SearchType.CLINIC) {
    return ['Local de atendimento', searchResult.city, searchResult.state].join(
      ' - ',
    );
  }

  if (searchResult.searchType === SearchType.BRANCH_LABORATORY) {
    return ['Laboratório', searchResult.city, searchResult.state].join(' - ');
  }

  return '';
};

const iconMap = {
  PRESENTIAL_CONSULTATION: faUserMd,
  ONLINE_CONSULTATION: faDesktop,
  LABORATORIAL_EXAM: faFlask,
  IMAGE_EXAM: faSignalStream,
  EMERGENCY: faKitMedical,
  VACCINE: faSyringe,
  SURGERY: faWavePulse,
  CLINIC: faHouseMedical,
  BRANCH_LABORATORY: faFlaskVial,
  DOCTOR: faUserMd,
};

const resolveIcon = (searchResult: SearchResult) => {
  if (
    searchResult.searchType
    && iconMap[searchResult.searchType as keyof typeof iconMap]
  ) {
    return iconMap[searchResult.searchType as keyof typeof iconMap];
  }

  return faUserMd;
};

interface SearchResultTabProps extends SearchResult, RecentSearches {
  setOpen: (open: boolean) => void;
  selectLaboratoryProcedure?: (procedure: SelectedProcedures) => void;
  locationOverride?: OverridedUserLocation;
  inRecentSearch?: boolean;
  searchTerm?: string;
}

export const SearchResultTab = ({
  locationOverride,
  inRecentSearch,
  searchTerm,
  ...searchResult
}: SearchResultTabProps) => {
  const router = useRouter();
  const { handleJump: jump }
    = useProgressiveModal<ProgressiveSearchModalKeys>();
  const {
    resolveLink,
    resolveName,
    isProcedureSearch,
  } = useSearch({ locationOverride });
  const [, setStoredRecentSearches] = useRecentSearchesStorage();
  const userLocation = useStore(
    userLocationStore,
    (state) => state.userLocation,
  );
  const { getParam } = useSearchParams([
    ...Object.values(PersistedUserNavigationSearchParams),
  ]);

  const analytics = getAnalyticsPlugin();

  const isLaboratorialOrVaccineSearch = checkLaboratorialOrVaccineSearch(
    searchResult.searchType as SearchType,
  );
  const Component = isLaboratorialOrVaccineSearch ? 'button' : 'a';

  const handleSearchResultClick = async (
    shouldNavigate: boolean = false,
    selectedLocation?: { city?: string; state?: string },
  ): Promise<void> => {
    if (isLaboratorialOrVaccineSearch) {
      searchResult.setOpen(true);
      await new Promise((resolve) => setTimeout(resolve, 100));
      jump({ key: ProgressiveSearchModalKeys.ASK_ADD_MORE_EXAMS });
      searchResult.selectLaboratoryProcedure?.({
        id: searchResult.id || '',
        code: searchResult.code || '',
        displayName: searchResult.displayName || '',
        examOrVaccineGroup: searchResult.examOrVaccineGroup?.map((item) => ({
          id: item.id!,
          code: item.code!,
          displayName: item.displayName!,
        })),
      });
      return;
    }

    await setSelectedSearch(searchResult);
    appendRecentSearches(searchResult, setStoredRecentSearches);
    searchResult.setOpen(false);

    if (shouldNavigate) {
      const linkSearchResult = resolveLink(searchResult);

      if (!selectedLocation) {
        router.push(linkSearchResult);
        return;
      }

      const linkUpdatedCityParams = new URL(linkSearchResult);
      linkUpdatedCityParams.searchParams.set(
        PersistedUserNavigationSearchParams.CIDADE, slugify(`${selectedLocation?.city}-${selectedLocation?.state}`));
      router.push(linkUpdatedCityParams.toString());
    }
  };

  const handleClick = async (e: React.MouseEvent): Promise<void> => {
    analytics.track(AnalyticsEnum.CLICK_RESULT_SEARCH_MODAL, {
      term: searchTerm || '',
      search_result_name: resolveName(searchResult),
    });

    const hasLocation = locationOverride || userLocation || getParam(PersistedUserNavigationSearchParams.CIDADE);
    const needsLocation = isProcedureSearch(searchResult.searchType as SearchType);

    if (!hasLocation && needsLocation) {
      searchResult.setOpen(false);
      e.preventDefault();
      await new Promise((resolve) => setTimeout(resolve, 100));
      setOpenLocationSelectionModal(true, {
        locationCallback: async (selectedLocation) => {
          await handleSearchResultClick(
            !isLaboratorialOrVaccineSearch,
            selectedLocation,
          );
        },
      });
      return;
    }

    if (isLaboratorialOrVaccineSearch) {
      await handleSearchResultClick();
      return;
    }

    await handleSearchResultClick(true);
  };

  const linkWithParams = resolveLink(searchResult);

  return (
    <div>
      <Component
        data-testid={DataTestIdEnum.PROCEDURES_MODAL_SEARCH_RESULT}
        href={linkWithParams}
        className="text-left w-full"
        onClick={handleClick}
      >
        <div className="flex py-5 cursor-pointer">
          {!searchResult.avatar
            ? (
                <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 min-w-12 min-h-12 rounded-lg border bg-green-50 border-gray-100">
                  <Icon
                    icon={resolveIcon(searchResult)}
                    className="text-green-500 max-w-6 max-h-6"
                    size="lg"
                  />
                </div>
              )
            : (
                <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 min-w-12 min-h-12 rounded-lg  border-gray-100">
                  <Avatar
                    src={searchResult.avatar}
                    alt={resolveName(searchResult)}
                    width={48}
                    height={48}
                  />
                </div>
              )}

          <div className="ml-4 flex flex-col">
            <span className="text-green-400 text-xs">
              {resolveTitle(searchResult, inRecentSearch)}
            </span>
            <span className="text-gray-800 font-semibold text-base">
              {resolveName(searchResult)}
            </span>
          </div>
        </div>
      </Component>
    </div>
  );
};
